import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  ArrowUpRight,
  ChevronRight,
  Clipboard,
  CreditCard,
  Globe2,
  KeyRound,
  LayoutDashboard,
  LockKeyhole,
  LogOut,
  Network,
  Plus,
  RefreshCw,
  Settings2,
  ShieldCheck,
  Ticket,
  UserCog,
  Users,
  Wifi,
  X,
} from "lucide-react";
import { api, type PanelModel } from "./api";

type AuthMode = "login" | "register";

interface AdminOverview {
  users: number;
  activeSubscriptions: number;
  activeNodes: number;
  unusedCards: number;
}

interface AdminUser {
  id: string;
  username: string;
  role: "admin" | "user";
  status: "active" | "disabled";
  valid_until: string;
}

interface AdminCard {
  id: string;
  duration_days: number;
  expires_at: string;
  status: "unused" | "redeemed" | "disabled";
  redeemed_at: string | null;
  redeemed_by: string | null;
  created_at: string;
}
interface AdminNode {
  id: string;
  label: string;
  country: string | null;
  source_kind: "managed_worker_vless" | "external_vless";
  status: "active" | "disabled" | "maintenance";
  health_url: string | null;
  last_checked_at: string | null;
  last_health: "healthy" | "unhealthy" | "unknown";
  created_at: string;
}

interface AuditEvent {
  action: string;
  object_type: string;
  object_id: string;
  created_at: string;
  actor: string | null;
}

interface AdminData {
  overview: AdminOverview;
  users: AdminUser[];
  cards: AdminCard[];
  nodes: AdminNode[];
  events: AuditEvent[];
}

interface AuthResponse {
  user: { id: string; username: string; role: "admin" | "user" };
}

const dateFormatter = new Intl.DateTimeFormat("zh-CN", { dateStyle: "medium", timeStyle: "short" });

function dateTime(value: string | null | undefined): string {
  if (!value) return "未检查";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "未知" : dateFormatter.format(parsed);
}

function normalizeError(error: unknown): string {
  return error instanceof Error ? error.message : "请求未完成";
}

async function copy(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch {
      // Fall through for browsers that reject the Clipboard API.
    }
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  textarea.remove();
  if (!copied) throw new Error("Copy failed");
}

export default function App() {
  const isAdminRoute = window.location.pathname === "/admin";
  const [model, setModel] = useState<PanelModel | null>(null);
  const [admin, setAdmin] = useState<AdminData | null>(null);
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const me = await api<PanelModel>("/me");
      setModel(me);
      if (isAdminRoute && me.user.role === "admin") {
        const [overview, users, nodes, cards, events] = await Promise.all([
          api<AdminOverview>("/admin/overview"),
          api<{ users: AdminUser[] }>("/admin/users"),
          api<{ nodes: AdminNode[] }>("/admin/nodes"),
          api<{ cards: AdminCard[] }>("/admin/cards"),
          api<{ events: AuditEvent[] }>("/admin/audit"),
        ]);
        setAdmin({ overview, users: users.users, cards: cards.cards, nodes: nodes.nodes, events: events.events });
      }
    } catch (error) {
      setModel(null);
      setAdmin(null);
      if (!(error instanceof Error && error.message === "Authentication required")) setNotice(normalizeError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [isAdminRoute]);

  async function submitAuth(payload: Record<string, string>, mode: AuthMode) {
    setWorking(true);
    try {
      const response = await api<AuthResponse>(mode === "register" ? "/auth/register" : "/auth/login", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      await load();
    } catch (error) {
      setNotice(normalizeError(error));
    } finally {
      setWorking(false);
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    setWorking(true);
    try {
      await api("/auth/password", {
        method: "POST",
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      setPasswordOpen(false);
      setNotice("\u5bc6\u7801\u5df2\u66f4\u65b0");
    } catch (error) {
      setNotice(normalizeError(error));
    } finally {
      setWorking(false);
    }
  }
  async function logout() {
    setWorking(true);
    try {
      await api("/auth/logout", { method: "POST", body: "{}" });
      setModel(null);
      setAdmin(null);
      window.location.assign("/");
    } catch (error) {
      setNotice(normalizeError(error));
    } finally {
      setWorking(false);
    }
  }

  const appTitle = isAdminRoute ? "管理" : "订阅";

  if (loading) return <LoadingScreen label={appTitle} />;
  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="Usage Control 首页">
          <span className="brand-mark"><Network size={18} strokeWidth={2.4} /></span>
          <span>usage</span><span className="brand-divider">/</span><span className="brand-context">{appTitle}</span>
        </a>
        <div className="topbar-actions">
          {model?.user.role === "admin" && <a className={`quiet-link ${isAdminRoute ? "active" : ""}`} href="/admin"><Settings2 size={15} /> 管理</a>}
          {model && <button className="icon-button" title={"\u4fee\u6539\u5bc6\u7801"} aria-label={"\u4fee\u6539\u5bc6\u7801"} onClick={() => setPasswordOpen(true)} disabled={working}><KeyRound size={16} /></button>}
          {model && <button className="icon-button" title="退出登录" onClick={() => void logout()} disabled={working}><LogOut size={16} /></button>}
        </div>
      </header>
      {notice && <Notice message={notice} onClose={() => setNotice(null)} />}
      {passwordOpen && model && <PasswordDialog working={working} onClose={() => setPasswordOpen(false)} onSubmit={changePassword} />}
      {!model ? <AuthGate mode={authMode} setMode={setAuthMode} working={working} onSubmit={submitAuth} /> : isAdminRoute ? (
        model.user.role === "admin" ? <AdminPanel data={admin} onReload={load} setNotice={setNotice} /> : <AccessDenied />
      ) : <UserPanel model={model} setNotice={setNotice} onReload={load} />}
    </div>
  );
}

function LoadingScreen({ label }: { label: string }) {
  return <div className="loading-screen"><div className="loading-mark"><Network size={21} /></div><p>加载 {label}</p></div>;
}

function Notice({ message, onClose }: { message: string; onClose: () => void }) {
  return <div className="notice" role="status"><span>{message}</span><button className="icon-button small" onClick={onClose} aria-label="关闭提示"><X size={15} /></button></div>;
}

function PasswordDialog({ working, onClose, onSubmit }: { working: boolean; onClose: () => void; onSubmit: (currentPassword: string, newPassword: string) => Promise<void> }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("\u4e24\u6b21\u5bc6\u7801\u4e0d\u4e00\u81f4");
      return;
    }
    setError(null);
    void onSubmit(currentPassword, newPassword);
  }

  return <div className="modal-backdrop" onMouseDown={(event) => { if (!working && event.currentTarget === event.target) onClose(); }}>
    <section className="password-dialog" role="dialog" aria-modal="true" aria-labelledby="password-title">
      <div className="dialog-header"><h2 id="password-title">{"\u4fee\u6539\u5bc6\u7801"}</h2><button className="icon-button small" type="button" title={"\u5173\u95ed"} aria-label={"\u5173\u95ed"} onClick={onClose} disabled={working}><X size={15} /></button></div>
      <form className="stack-form" onSubmit={submit}>
        <Field label={"\u5f53\u524d\u5bc6\u7801"} value={currentPassword} onChange={setCurrentPassword} type="password" autoComplete="current-password" />
        <Field label={"\u65b0\u5bc6\u7801"} value={newPassword} onChange={setNewPassword} type="password" autoComplete="new-password" placeholder={"\u81f3\u5c11 12 \u5b57\u7b26"} minLength={12} />
        <Field label={"\u786e\u8ba4\u5bc6\u7801"} value={confirmPassword} onChange={setConfirmPassword} type="password" autoComplete="new-password" minLength={12} />
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button" type="submit" disabled={working}>{working ? "\u5904\u7406\u4e2d" : "\u66f4\u65b0\u5bc6\u7801"}</button>
      </form>
    </section>
  </div>;
}
function AuthGate({ mode, setMode, working, onSubmit }: { mode: AuthMode; setMode: (mode: AuthMode) => void; working: boolean; onSubmit: (payload: Record<string, string>, mode: AuthMode) => Promise<void> }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    void onSubmit({ username, password }, mode);
  };
  const title = mode === "register" ? "注册" : "登录";
  return (
    <main className="auth-layout">
      <section className="auth-intro"><h1>节点订阅</h1></section>
      <section className="auth-panel" aria-label={title}>
        <div className="auth-panel-header"><h2>{title}</h2></div>
        <form onSubmit={submit} className="stack-form">
          <Field label="用户名" value={username} onChange={setUsername} autoComplete="username" placeholder="3-32 字符" />
          <Field label="密码" value={password} onChange={setPassword} type="password" autoComplete={mode === "register" ? "new-password" : "current-password"} placeholder="至少 12 字符" />
          <button className="primary-button" type="submit" disabled={working}>{working ? "处理中" : mode === "register" ? "创建账户" : "登录"}<ChevronRight size={16} /></button>
        </form>
        <div className="auth-switches">
          {mode !== "login" && <button type="button" className="text-button" onClick={() => setMode("login")}>登录</button>}
          {mode !== "register" && <button type="button" className="text-button" onClick={() => setMode("register")}>注册</button>}
        </div>
      </section>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, autoComplete, minLength, optional = false }: { label: string; value: string; onChange: (value: string) => void; type?: string; placeholder?: string; autoComplete?: string; minLength?: number; optional?: boolean }) {
  return <label className="field"><span>{label}{optional && <em>可选</em>}</span><input type={type} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} autoComplete={autoComplete} minLength={minLength} required={!optional} /></label>;
}

function UserPanel({ model, setNotice, onReload }: { model: PanelModel; setNotice: (message: string) => void; onReload: () => Promise<void> }) {
  const [cardCode, setCardCode] = useState("");
  const [working, setWorking] = useState(false);

  async function redeem(event: FormEvent) {
    event.preventDefault();
    setWorking(true);
    try {
      await api("/cards/redeem", { method: "POST", body: JSON.stringify({ code: cardCode }) });
      setCardCode("");
      setNotice("已续期 30 天");
      await onReload();
    } catch (error) {
      setNotice(normalizeError(error));
    } finally {
      setWorking(false);
    }
  }

  const activeNodes = model.nodes.filter((node) => node.status === "active").length;
  return <main className="content-wrap user-page">
    <section className="page-heading">
      <div><p className="eyebrow">ACCOUNT / {model.user.username.toUpperCase()}</p><h1>订阅</h1></div>
      <div className="heading-actions"><button className="quiet-button" onClick={() => void onReload()}><RefreshCw size={15} /> 刷新</button><span className="role-pill"><UserCog size={14} /> {model.user.role === "admin" ? "管理员" : "账户"}</span></div>
    </section>
    <section className="status-band">
      <div className="status-primary"><span className={`status-dot ${model.subscription.active ? "good" : "bad"}`} /><div><p>{model.subscription.active ? "订阅有效" : "订阅已到期"}</p><strong>{model.subscription.remainingDays} <small>天</small></strong></div></div>
      <div className="status-metric"><span>到期</span><b>{dateTime(model.subscription.validUntil)}</b></div>
      <div className="status-metric"><span>节点</span><b>{activeNodes} / {model.nodes.length}</b></div>
    </section>
    <div className="user-grid">
      <section className="panel subscription-panel">
        <PanelTitle icon={<KeyRound size={17} />} index="01" title="订阅链接" />
        <div className="subscription-url"><code>{model.subscription.url}</code><button className="icon-button solid" title="复制订阅链接" onClick={() => void copy(model.subscription.url).then(() => setNotice("已复制"))}><Clipboard size={16} /></button></div>
        <div className="subscription-footer"><span>固定链接 · {model.subscription.cycleDays} 天周期</span><a href={model.subscription.url} target="_blank" rel="noreferrer">打开 <ArrowUpRight size={14} /></a></div>
      </section>
      <section className="panel redeem-panel">
        <PanelTitle icon={<Ticket size={17} />} index="02" title="卡密兑换" />
        <form onSubmit={redeem} className="redeem-form"><label className="field"><span>卡密</span><input value={cardCode} onChange={(event) => setCardCode(event.target.value)} placeholder="粘贴卡密" minLength={16} required /></label><button className="primary-button compact" type="submit" disabled={working}><CreditCard size={16} /> 续期 30 天</button></form>
      </section>
    </div>
    <section className="panel nodes-panel">
      <PanelTitle icon={<Globe2 size={17} />} index="03" title="节点" />
      <div className="table-wrap"><table><thead><tr><th>节点</th><th>地区</th><th>状态</th><th>检查</th></tr></thead><tbody>{model.nodes.map((node) => <tr key={node.id}><td><div className="node-name"><Wifi size={15} /> {node.label}</div></td><td>{node.country || "未标注"}</td><td><StatusBadge value={node.status === "active" ? node.last_health : node.status} /></td><td>{dateTime(node.last_checked_at)}</td></tr>)}</tbody></table></div>
    </section>
  </main>;
}

function PanelTitle({ icon, index, title }: { icon: ReactNode; index: string; title: string }) {
  return <div className="panel-title"><span className="panel-icon">{icon}</span><div><p className="section-index">{index}</p><h2>{title}</h2></div></div>;
}

function StatusBadge({ value }: { value: string }) {
  const normalized = value === "healthy" || value === "active" ? "healthy" : value === "unhealthy" || value === "disabled" ? "unhealthy" : "unknown";
  const label = normalized === "healthy" ? "可用" : normalized === "unhealthy" ? "不可用" : value === "maintenance" ? "维护" : "待检查";
  return <span className={`status-badge ${normalized}`}>{label}</span>;
}

function CardBadge({ status }: { status: AdminCard["status"] }) {
  const label = status === "unused" ? "\u672a\u4f7f\u7528" : status === "redeemed" ? "\u5df2\u5151\u6362" : "\u5df2\u505c\u7528";
  return <span className={`card-badge ${status}`}>{label}</span>;
}
function CardSecretDialog({ cards, onClose, setNotice }: { cards: string[]; onClose: () => void; setNotice: (message: string) => void }) {
  const [copiedCard, setCopiedCard] = useState<string | null>(null);

  async function copyCard(card: string) {
    try {
      await copy(card);
      setCopiedCard(card);
      setNotice("\u5df2\u590d\u5236");
    } catch (error) {
      setNotice(normalizeError(error));
    }
  }

  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
    <section className="card-secret-dialog" role="dialog" aria-modal="true" aria-labelledby="card-secret-title">
      <div className="dialog-header"><h2 id="card-secret-title">{"\u65b0\u5361\u5bc6"}</h2><button className="icon-button small" type="button" title={"\u5173\u95ed"} aria-label={"\u5173\u95ed"} onClick={onClose}><X size={15} /></button></div>
      <div className="card-secret-list">{cards.map((card, index) => <SecretOutput key={card} label={`${"\u5361\u5bc6"} ${index + 1}`} value={card} onCopy={() => void copyCard(card)} />)}</div>
      {copiedCard && <p className="card-copy-state" role="status">{"\u5df2\u590d\u5236"}</p>}
      <button className="primary-button" type="button" onClick={onClose}>{"\u5b8c\u6210"}</button>
    </section>
  </div>;
}
function AccessDenied() {
  return <main className="content-wrap empty-state"><LockKeyhole size={24} /><h1>无权限</h1><a className="primary-button" href="/">返回 <ChevronRight size={16} /></a></main>;
}

function AdminPanel({ data, onReload, setNotice }: { data: AdminData | null; onReload: () => Promise<void>; setNotice: (message: string) => void }) {
  const [working, setWorking] = useState(false);
  const [createdCards, setCreatedCards] = useState<string[]>([]);
  const [cardSecretOpen, setCardSecretOpen] = useState(false);
  const [lastNodeId, setLastNodeId] = useState<string | null>(null);
  const [nodeForm, setNodeForm] = useState({ sourceKind: "managed_worker_vless", label: "", country: "", hostname: "", wsPath: "/connect/", externalUri: "", healthUrl: "" });
  const [accountView, setAccountView] = useState<"users" | "cards">("users");
  if (!data) return <LoadingScreen label="管理" />;

  async function mutate(action: () => Promise<void>, refresh = true) {
    setWorking(true);
    try {
      await action();
      if (refresh) await onReload();
    } catch (error) {
      setNotice(normalizeError(error));
    } finally {
      setWorking(false);
    }
  }

  function closeCardSecrets() {
    setCardSecretOpen(false);
    setCreatedCards([]);
    void onReload();
  }
  return <main className="content-wrap admin-page">
    {cardSecretOpen && createdCards.length > 0 && <CardSecretDialog cards={createdCards} setNotice={setNotice} onClose={closeCardSecrets} />}
    <section className="page-heading"><div><p className="eyebrow">OPERATIONS / ADMIN</p><h1>管理</h1></div><div className="heading-actions"><button className="quiet-button" onClick={() => void onReload()}><RefreshCw size={15} /> 刷新</button><span className="role-pill"><ShieldCheck size={14} /> 管理员</span></div></section>
    <section className="metrics-grid"><Metric icon={<Users size={18} />} label="用户" value={data.overview.users} /><Metric icon={<ShieldCheck size={18} />} label="订阅" value={data.overview.activeSubscriptions} /><Metric icon={<Network size={18} />} label="节点" value={data.overview.activeNodes} /><Metric icon={<Ticket size={18} />} label="卡密" value={data.overview.unusedCards} /></section>
    <section className="panel management-panel">
      <PanelTitle icon={<Users size={17} />} index="01" title={"\u8d26\u6237"} />
      <div className="format-tabs management-tabs" role="tablist">
        <button type="button" className={accountView === "users" ? "selected" : ""} onClick={() => setAccountView("users")}>{"\u7528\u6237"}</button>
        <button type="button" className={accountView === "cards" ? "selected" : ""} onClick={() => setAccountView("cards")}>{"\u5361\u5bc6"}</button>
      </div>
      {accountView === "users" ? <div className="table-wrap"><table><thead><tr><th>{"\u7528\u6237"}</th><th>{"\u89d2\u8272"}</th><th>{"\u5230\u671f"}</th><th>{"\u72b6\u6001"}</th><th aria-label={"\u64cd\u4f5c"} /></tr></thead><tbody>{data.users.map((user) => <tr key={user.id}><td>{user.username}</td><td>{user.role === "admin" ? "\u7ba1\u7406\u5458" : "\u7528\u6237"}</td><td>{dateTime(user.valid_until)}</td><td><StatusBadge value={user.status} /></td><td><div className="table-actions"><button className="table-action" disabled={working} onClick={() => void mutate(async () => { await api(`/admin/users/${user.id}/extend`, { method: "POST", body: "{}" }); setNotice("\u5df2\u7eed\u671f 30 \u5929"); })}>+30 {"\u5929"}</button><button className="table-action" disabled={working} onClick={() => void mutate(async () => { await api(`/admin/users/${user.id}`, { method: "PATCH", body: JSON.stringify({ status: user.status === "active" ? "disabled" : "active" }) }); setNotice("\u5df2\u66f4\u65b0"); })}>{user.status === "active" ? "\u505c\u7528" : "\u542f\u7528"}</button></div></td></tr>)}</tbody></table></div> : <div className="card-management">
        <div className="card-toolbar"><button className="primary-button compact" disabled={working} onClick={() => void mutate(async () => { const result = await api<{ cards: Array<{ code: string }> }>("/admin/cards", { method: "POST", body: JSON.stringify({ quantity: 1, expiresInDays: 90 }) }); const codes = result.cards.map((card) => card.code); setCreatedCards(codes); setCardSecretOpen(true); setNotice("\u5df2\u521b\u5efa 30 \u5929\u5361\u5bc6"); }, false)}><Ticket size={15} /> {"\u521b\u5efa 30 \u5929"}</button></div>
        <div className="table-wrap"><table><thead><tr><th>ID</th><th>{"\u65f6\u957f"}</th><th>{"\u6709\u6548\u81f3"}</th><th>{"\u72b6\u6001"}</th><th>{"\u7528\u6237"}</th><th aria-label={"\u64cd\u4f5c"} /></tr></thead><tbody>{data.cards.map((card) => <tr key={card.id}><td><code className="short-id">{card.id.slice(0, 8)}</code></td><td>{card.duration_days} {"\u5929"}</td><td>{dateTime(card.expires_at)}</td><td><CardBadge status={card.status} /></td><td>{card.redeemed_by || "-"}</td><td><div className="table-actions">{card.status === "unused" && <button className="table-action" disabled={working} onClick={() => void mutate(async () => { await api(`/admin/cards/${card.id}`, { method: "PATCH", body: JSON.stringify({ status: "disabled" }) }); setNotice("\u5df2\u505c\u7528"); })}>{"\u505c\u7528"}</button>}</div></td></tr>)}</tbody></table></div>
      </div>}
    </section>
    <section className="panel node-management">
      <PanelTitle icon={<Network size={17} />} index="02" title={"\u8282\u70b9"} />
      <div className="format-tabs source-tabs" role="tablist">
        <button type="button" className={nodeForm.sourceKind === "managed_worker_vless" ? "selected" : ""} onClick={() => setNodeForm({ ...nodeForm, sourceKind: "managed_worker_vless" })}>Worker</button>
        <button type="button" className={nodeForm.sourceKind === "external_vless" ? "selected" : ""} onClick={() => setNodeForm({ ...nodeForm, sourceKind: "external_vless" })}>{"\u5916\u90e8"}</button>
      </div>
      <form className="node-form" onSubmit={(event) => {
        event.preventDefault();
        void mutate(async () => {
          const config = nodeForm.sourceKind === "managed_worker_vless" ? { hostname: nodeForm.hostname, wsPath: nodeForm.wsPath, port: 443 } : { uri: nodeForm.externalUri };
          const result = await api<{ id: string }>("/admin/nodes", { method: "POST", body: JSON.stringify({ label: nodeForm.label, country: nodeForm.country, healthUrl: nodeForm.healthUrl || undefined, sourceKind: nodeForm.sourceKind, config }) });
          setLastNodeId(result.id);
          setNodeForm({ sourceKind: "managed_worker_vless", label: "", country: "", hostname: "", wsPath: "/connect/", externalUri: "", healthUrl: "" });
          setNotice("\u5df2\u6dfb\u52a0");
        });
      }}>
        <div className="two-fields"><Field label={"\u540d\u79f0"} value={nodeForm.label} onChange={(label) => setNodeForm({ ...nodeForm, label })} /><Field label={"\u5730\u533a"} value={nodeForm.country} onChange={(country) => setNodeForm({ ...nodeForm, country })} optional /></div>
        {nodeForm.sourceKind === "managed_worker_vless" ? <div className="two-fields"><Field label={"\u57df\u540d"} value={nodeForm.hostname} onChange={(hostname) => setNodeForm({ ...nodeForm, hostname })} placeholder="node.example.workers.dev" /><Field label={"\u8def\u5f84"} value={nodeForm.wsPath} onChange={(wsPath) => setNodeForm({ ...nodeForm, wsPath })} placeholder="/connect/opaque-path" /></div> : <Field label="VLESS URI" value={nodeForm.externalUri} onChange={(externalUri) => setNodeForm({ ...nodeForm, externalUri })} placeholder="vless://uuid@host:443?encryption=none&security=tls&type=ws&path=/connect" />}
        <Field label={"\u68c0\u67e5 URL"} value={nodeForm.healthUrl} onChange={(healthUrl) => setNodeForm({ ...nodeForm, healthUrl })} placeholder="https://node.example.workers.dev/healthz" optional />
        <button className="primary-button compact" disabled={working} type="submit"><Plus size={15} /> {"\u6dfb\u52a0"}</button>
      </form>
      {lastNodeId && <SecretOutput label="NODE_ID" value={lastNodeId} onCopy={() => void copy(lastNodeId).then(() => setNotice("\u5df2\u590d\u5236"))} />}
      <div className="node-list"><div className="table-wrap"><table><thead><tr><th>{"\u8282\u70b9"}</th><th>{"\u7c7b\u578b"}</th><th>{"\u5065\u5eb7"}</th><th>{"\u72b6\u6001"}</th><th aria-label={"\u64cd\u4f5c"} /></tr></thead><tbody>{data.nodes.map((node) => <tr key={node.id}><td><div className="node-name"><Wifi size={15} /> {node.label}<small>{node.country || "\u672a\u6807\u6ce8"}</small></div></td><td>{node.source_kind === "managed_worker_vless" ? "Worker" : "\u5916\u90e8"}</td><td><StatusBadge value={node.last_health} /></td><td>{node.status === "active" ? "\u542f\u7528" : node.status === "maintenance" ? "\u7ef4\u62a4" : "\u505c\u7528"}</td><td><select className="status-select" value={node.status} disabled={working} onChange={(event) => void mutate(async () => { await api(`/admin/nodes/${node.id}`, { method: "PATCH", body: JSON.stringify({ status: event.target.value }) }); setNotice("\u5df2\u66f4\u65b0"); })}><option value="active">{"\u542f\u7528"}</option><option value="maintenance">{"\u7ef4\u62a4"}</option><option value="disabled">{"\u505c\u7528"}</option></select></td></tr>)}</tbody></table></div></div>
    </section>
    <section className="panel audit-panel">
      <PanelTitle icon={<LayoutDashboard size={17} />} index="03" title="记录" />
      <div className="audit-list">{data.events.map((event) => <div className="audit-item" key={`${event.object_id}-${event.created_at}`}><span className="audit-time">{dateTime(event.created_at)}</span><b>{event.actor || "system"}</b><span>{event.action.replaceAll("_", " ")}</span><span className="audit-object">{event.object_type} / {event.object_id.slice(0, 8)}</span></div>)}</div>
    </section>
  </main>;
}

function Metric({ icon, label, value }: { icon: ReactNode; label: string; value: number }) {
  return <div className="metric"><span>{icon}</span><div><p>{label}</p><strong>{value}</strong></div></div>;
}

function SecretOutput({ label, value, onCopy }: { label: string; value: string; onCopy: () => void }) {
  return <div className="secret-output"><span>{label}</span><code>{value}</code><button type="button" className="icon-button solid" title="复制" onClick={onCopy}><Clipboard size={15} /></button></div>;
}
